import { Stack } from "@mui/system";
import { SingleCaption } from "./single-caption";
import { Button, Chip, FormControlLabel, InputAdornment, OutlinedInput, Switch, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState, useRef, memo } from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Caption } from "./scribetool";
import { VariableSizeList as List } from "react-window";
import { areEqual } from "react-window";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";

const ItemRenderer: React.FC<any> = ({ data, index, style }) => {
  const {
    captions,
    onUpdateCaption,
    onDeletaCaption,
    setEditSegmentId,
    editSegmentId,
    dragSegmentId,
    triggerUpdate,
    currentCaptionIndex,
    scrollToTrigger,
    setScrollToTrigger,
    onClickTimecode,
    mode,
    videoFps,
    currentTime,
    setRowHeight,
    search,
    currentFoundsentenceIndex,
  } = data;

  const render = useMemo(
    () => (
      <SingleCaption
        setRowHeight={setRowHeight}
        key={data.captions[index].segmentId}
        caption={captions[index]}
        onUpdateCaption={onUpdateCaption}
        onDeletaCaption={onDeletaCaption}
        setEditSegmentId={setEditSegmentId}
        triggerUpdate={
          captions[index].segmentId === editSegmentId || captions[index].segmentId === dragSegmentId
            ? triggerUpdate
            : false
        }
        isEditing={captions[index].segmentId === editSegmentId}
        active={currentCaptionIndex === index}
        scrollTo={scrollToTrigger === index}
        setScrollTo={setScrollToTrigger}
        onClickTimecode={onClickTimecode}
        currentTime={currentCaptionIndex === index ? currentTime : null}
        videoFps={videoFps}
        mode={mode}
        index={index}
        height={style.height}
        left={style.left}
        position={style.position}
        right={style.right}
        top={style.top}
        showSpeaker={index > 0 && captions[index - 1].speaker === captions[index].speaker ? false : true}
        search={search}
        isCurrentSearchSentence={currentFoundsentenceIndex === index}
      />
    ),
    [
      style?.top,
      style?.height,
      index,
      currentCaptionIndex === index,
      scrollToTrigger === index,
      currentCaptionIndex === index ? currentTime : null,
      search,
      currentFoundsentenceIndex === index,
      editSegmentId,
      triggerUpdate,
    ]
  );
  return render;
};

const Row = memo(ItemRenderer, areEqual);
export interface SubtitlesListProps {
  captions: Caption[];
  onUpdateCaption?: (segmentId: string, text: string) => void;
  onDeletaCaption?: (segmentId: string) => void;
  onCreateCaption?: () => void;
  editSegmentId?: string | null;
  setEditSegmentId?: any;
  triggerUpdate?: boolean;
  dragSegmentId?: string | null;
  currentCaptionIndex: number;
  onClickTimecode: (sec: number) => void;
  currentTime: number;
  videoFps: number;
  mode: "stp" | "sub" | "project";
}

export const SubtitlesList: React.FC<SubtitlesListProps> = (props) => {
  const { t } = useTranslation();
  const {
    captions,
    editSegmentId,
    onDeletaCaption,
    onUpdateCaption,
    onCreateCaption,
    setEditSegmentId,
    triggerUpdate,
    dragSegmentId,
    currentCaptionIndex,
    onClickTimecode,
    currentTime,
    videoFps,
    mode,
  } = props;

  const [scrollToTrigger, setScrollToTrigger] = useState<number>(currentCaptionIndex);
  const [width, setWidth] = useState(100);
  const [height, setHeight] = useState(100);

  const listContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      event[0].contentBoxSize[0].inlineSize && setWidth(event[0].contentBoxSize[0].inlineSize);
      event[0].contentBoxSize[0].blockSize && setHeight(event[0].contentBoxSize[0].blockSize);
    });

    if (listContainerRef.current) resizeObserver.observe(listContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [listContainerRef.current]);

  const theme = useTheme();
  const listRef = React.createRef<any>();

  const handleBackToTop = () => {
    setScrollToTrigger(0);
  };

  const handleGoToCurrent = () => {
    if (currentCaptionIndex >= 0) setScrollToTrigger(currentCaptionIndex);
    else {
      const currentTimeSec = currentTime;
      const closerCaptionIndex = captions.findIndex(
        (x, i) => x.secOut <= currentTimeSec && (i >= captions.length - 1 || captions[i + 1].secIn >= currentTimeSec)
      );
      if (closerCaptionIndex >= 0) setScrollToTrigger(closerCaptionIndex);
    }
  };

  const handleGoToEnd = () => {
    setScrollToTrigger(captions.length - 1);
  };

  const rowHeights = useRef<any>({});

  function getRowHeight(index) {
    return rowHeights.current[index] ?? 32;
  }

  function setRowHeight(index, size) {
    if (listRef.current && rowHeights.current[index] !== size) listRef.current.resetAfterIndex(index);
    rowHeights.current = {
      ...rowHeights.current,
      [index]: size,
      maxIndex: index > (rowHeights.current.maxIndex ?? -1) ? index : rowHeights.current.maxIndex ?? -1,
    };
  }

  const controlsMemo = useMemo(() => {
    return (
      <Stack
        justifyContent={"space-between"}
        position={"sticky"}
        direction={"row"}
        display={"flex"}
        zIndex={1200}
        spacing={1}
        top={0}
        right={0}
        left={0}
        height={"48px"}
        pt={2}
      >
        <Button sx={{ width: "67px" }} startIcon={<ArrowUpwardIcon />} onClick={handleBackToTop}>
          {t("common:first")}
        </Button>

        <Button fullWidth variant="outlined" startIcon={<ReadMoreIcon />} onClick={handleGoToCurrent}>
          {t("common:current")}
        </Button>
        <Button sx={{ width: "67px" }} endIcon={<ArrowDownwardIcon />} onClick={handleGoToEnd}>
          {t("button:last")}
        </Button>
      </Stack>
    );
  }, [theme.palette.mode, currentCaptionIndex]);

  useEffect(() => {
    if (scrollToTrigger >= 0) listRef.current?.scrollToItem(scrollToTrigger, "center");
  }, [scrollToTrigger]);
  useEffect(() => {
    if (currentCaptionIndex >= 0) listRef.current?.scrollToItem(currentCaptionIndex, "center");
  }, [currentCaptionIndex]);

  const [search, setSearch] = useState("");
  const [currentFoundIndex, setCurrentFoundIndex] = useState(-1);
  const [showOnlyResults, setShowOnlyResults] = useState(false);

  const handleChangeShowOnlyResult = () => {
    setShowOnlyResults((x) => !x);
  };
  const searchActive = search.length > 2;

  const foundSentencesIndexes = useMemo(() => {
    const result: number[] = [];
    captions.forEach((x, i) => {
      if (
        x.words?.find((w) => {
          const text = w.text.trim().toLowerCase();
          return text.includes(search);
        })
      )
        result.push(i);
    });
    return result;
  }, [search]);

  useEffect(() => {
    if (currentFoundIndex > -1) {
      setScrollToTrigger(foundSentencesIndexes[currentFoundIndex]);
    }
  }, [currentFoundIndex]);

  useEffect(() => {
    setCurrentFoundIndex(-1);
  }, [foundSentencesIndexes.length]);

  return (
    <>
      <Stack direction={"row"} display={"flex"} spacing={0.5} width={"100%"}>
        <OutlinedInput
          label={t("common:search")}
          sx={{ flex: 1 }}
          placeholder={t("common:search")}
          onChange={(e) => setSearch(e.currentTarget.value)}
          endAdornment={
            <InputAdornment position="end">
              <SearchIcon />
            </InputAdornment>
          }
        />
        {searchActive && (
          <FormControlLabel
            sx={{ minWidth: "80px" }}
            control={<Switch checked={showOnlyResults} onChange={handleChangeShowOnlyResult} />}
            label={t("form:label.onlyResults")}
          />
        )}
      </Stack>
      <Stack
        width={"100%"}
        height={searchActive && !showOnlyResults ? 36 : 0}
        display={searchActive && !showOnlyResults ? "flex" : "none"}
        p={0.5}
        spacing={1}
        zIndex={1299}
        direction={"row"}
      >
        {foundSentencesIndexes.length > 1 && (
          <Button
            onClick={(e) => {
              setCurrentFoundIndex((x) => (x <= 0 ? foundSentencesIndexes.length - 1 : x - 1));
            }}
            sx={{ height: "100%" }}
            startIcon={<ArrowLeft />}
          >
            {t("button:previous")}
          </Button>
        )}
        <Chip
          sx={{ height: "100%" }}
          label={t("chip:label.matchCounter", {
            count: foundSentencesIndexes.length,
            current: currentFoundIndex + 1,
            total: foundSentencesIndexes.length,
          })}
        />
        {foundSentencesIndexes.length === 1 && (
          <Button
            startIcon={<ReadMoreIcon />}
            onClick={() => {
              setCurrentFoundIndex(0);
            }}
          ></Button>
        )}
        {foundSentencesIndexes.length > 1 && (
          <Button
            onClick={(e) => {
              setCurrentFoundIndex((x) => (x === foundSentencesIndexes.length - 1 ? 0 : x + 1));
            }}
            sx={{ height: "100%" }}
            endIcon={<ArrowRight />}
          >
            {t("common:next")}
          </Button>
        )}
      </Stack>
      <Stack
        height={searchActive && !showOnlyResults ? "calc(100% - 42px)" : "100%"}
        ref={listContainerRef}
        width={"100%"}
        direction="column"
        position={"relative"}
      >
        <List
          className="List"
          itemSize={getRowHeight}
          estimatedItemSize={32}
          ref={listRef}
          height={height}
          width={width}
          style={{
            overflowY: "scroll",
            scrollbarColor: `${
              theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[300]
            } transparent`,
            marginLeft: 1,
            marginTop: "0px !important",
            scrollbarWidth: "thin",
          }}
          itemData={{
            captions:
              searchActive && showOnlyResults
                ? captions.filter((x, i) => foundSentencesIndexes.find((n) => n === i))
                : captions,
            onUpdateCaption: onUpdateCaption,
            onDeletaCaption: onDeletaCaption,
            setEditSegmentId: setEditSegmentId,
            triggerUpdate: triggerUpdate,
            editSegmentId: editSegmentId,
            currentCaptionIndex: currentCaptionIndex,
            scrollToTrigger: scrollToTrigger,
            setScrollToTrigger: setScrollToTrigger,
            onClickTimecode: onClickTimecode,
            currentTime: currentTime,
            videoFps: videoFps,
            mode: mode,
            dragSegmentId: dragSegmentId,
            setRowHeight: setRowHeight,
            search: search,
            currentFoundsentenceIndex: foundSentencesIndexes[currentFoundIndex],
          }}
          itemKey={(index, data) => data.captions[index].segmentId}
          itemCount={searchActive && showOnlyResults ? foundSentencesIndexes.length : captions.length}
          overscanCount={15}
        >
          {Row}
        </List>
        {controlsMemo}
      </Stack>
    </>
  );
};
